import { Client, IMessage, ReconnectionTimeMode, StompSubscription, TickerStrategy } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import DialerTreeConfig from "@/app/config";
import { authService } from "@/services/authService";
import axiosInstance, { refreshTokens, refreshTokensIfExpired } from "@/store/api";
import {
  EventReplayResponse,
  ReplayedEvent,
  SocketConnectionState,
  SocketConnectionStatus,
  SocketMessage,
  SocketResync,
} from "@/model/interface/SocketConnection";

const RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 10000;
const CONNECTION_TIMEOUT_MS = 10000;
const STALE_AFTER_MS = 20000;
const FULL_REFRESH_AFTER_MS = 120000;
const REPLAY_TIMEOUT_MS = 10000;
const USER_QUEUE_PREFIX = "/user/queue/";

interface SocketSubscription {
  destination: string;
  onMessage: (message: SocketMessage) => void;
  onSubscribed?: () => void;
  stompSubscription: StompSubscription | null;
}

interface HeldMessage {
  subscription: SocketSubscription;
  message: SocketMessage;
  seq: number;
}

const subscriptions = new Set<SocketSubscription>();
const stateListeners = new Set<() => void>();
const resyncListeners = new Set<(resync: SocketResync) => void>();

let state: SocketConnectionState = { status: "connecting", disconnectedAt: null, generation: 0 };
let started = false;
let isConnected = false;
let waitingForReconnect = false;
let lastFrameAt = 0;
let gapStartedAt: number | null = null;
let hiddenDuringGap = false;
let lastSeq: number | null = null;
let epoch: string | null = null;
let replayInFlight = false;
let heldMessages: HeldMessage[] = [];

const publishState = (next: SocketConnectionState) => {
  if (
    next.status === state.status &&
    next.disconnectedAt === state.disconnectedAt &&
    next.generation === state.generation
  ) {
    return;
  }
  state = next;
  stateListeners.forEach((listener) => listener());
};

const disconnectedStatus = (): SocketConnectionStatus => {
  if (!navigator.onLine) {
    return "offline";
  }
  return state.generation > 0 ? "reconnecting" : "connecting";
};

const notifyResync = (now: number) => {
  const gapMs = gapStartedAt === null ? null : now - gapStartedAt;
  const wasHidden = hiddenDuringGap;
  gapStartedAt = null;
  hiddenDuringGap = false;
  if (gapMs === null) {
    return;
  }
  const resync: SocketResync = { gapMs, wasHidden, fullRefresh: gapMs > FULL_REFRESH_AFTER_MS || wasHidden };
  resyncListeners.forEach((listener) => listener(resync));
};

const trackSequence = (destination: string, headers: Record<string, string>): number | null => {
  if (!destination.startsWith(USER_QUEUE_PREFIX)) {
    return null;
  }
  const seq = Number(headers["seq"]);
  if (!Number.isFinite(seq)) {
    return null;
  }
  const messageEpoch = headers["epoch"] ?? epoch;
  if (messageEpoch !== epoch || lastSeq === null) {
    epoch = messageEpoch;
    lastSeq = seq;
  } else {
    lastSeq = Math.max(lastSeq, seq);
  }
  return seq;
};

const deliver = (subscription: SocketSubscription, message: IMessage) => {
  const socketMessage: SocketMessage = { body: message.body, headers: message.headers, replayed: false };
  const seq = trackSequence(subscription.destination, message.headers);
  if (replayInFlight && seq !== null) {
    heldMessages.push({ subscription, message: socketMessage, seq });
    return;
  }
  subscription.onMessage(socketMessage);
};

const routeReplayedEvent = (event: ReplayedEvent) => {
  const destination = `${USER_QUEUE_PREFIX}${event.queue}`;
  const message: SocketMessage = {
    body: JSON.stringify(event.payload),
    headers: { seq: String(event.seq), "sent-at": event.sentAt },
    replayed: true,
  };
  subscriptions.forEach((subscription) => {
    if (subscription.destination === destination) {
      subscription.onMessage(message);
    }
  });
};

const releaseHeldMessages = (replayedUpTo: number | null) => {
  const held = heldMessages;
  heldMessages = [];
  held.forEach((heldMessage) => {
    if (replayedUpTo === null || heldMessage.seq > replayedUpTo) {
      heldMessage.subscription.onMessage(heldMessage.message);
    }
  });
};

const replayEvents = async (now: number) => {
  const gapStart = gapStartedAt;
  const knownEpoch = epoch;
  let replayedUpTo: number | null = null;
  try {
    const response = await axiosInstance.get<{ data: EventReplayResponse }>(
      `${DialerTreeConfig.baseAPIEndpoint}/v1/events`,
      { params: { after: lastSeq }, timeout: REPLAY_TIMEOUT_MS },
    );
    const replay = response.data.data;
    if (replay.replayable && replay.epoch === knownEpoch) {
      replay.events.forEach((event) => routeReplayedEvent(event));
      lastSeq = Math.max(lastSeq ?? 0, replay.latestSeq);
      replayedUpTo = replay.latestSeq;
      console.info(`Socket replayed ${replay.events.length} events`);
    }
  } catch (error) {
    console.error("Socket replay failed", error);
  }
  replayInFlight = false;
  if (replayedUpTo === null) {
    notifyResync(now);
  } else if (gapStartedAt === gapStart) {
    gapStartedAt = null;
    hiddenDuringGap = false;
  }
  releaseHeldMessages(replayedUpTo);
};

const recoverGap = (now: number) => {
  if (gapStartedAt !== null && lastSeq !== null && epoch !== null) {
    replayInFlight = true;
    void replayEvents(now);
    return;
  }
  notifyResync(now);
};

const client = new Client({
  webSocketFactory: () => new SockJS(DialerTreeConfig.baseWSEndpoint),
  reconnectDelay: RECONNECT_DELAY_MS,
  maxReconnectDelay: MAX_RECONNECT_DELAY_MS,
  reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
  connectionTimeout: CONNECTION_TIMEOUT_MS,
  discardWebsocketOnCommFailure: true,
  heartbeatStrategy: TickerStrategy.Worker,
  beforeConnect: async () => {
    waitingForReconnect = false;
    if (authService.isAccessTokenExpired()) {
      await refreshTokens().catch(() => undefined);
    }
    const bearer = authService.getBearerToken();
    client.connectHeaders = bearer ? { Authorization: bearer } : {};
  },
  onConnect: () => {
    const now = Date.now();
    lastFrameAt = now;
    isConnected = true;
    if (state.disconnectedAt !== null) {
      console.info(`Socket reconnected after ${Math.round((now - state.disconnectedAt) / 1000)} s`);
    }
    publishState({ status: "connected", disconnectedAt: null, generation: state.generation + 1 });
    subscriptions.forEach((subscription) => attach(subscription));
    recoverGap(now);
  },
  onWebSocketClose: () => {
    const lostConnection = isConnected;
    isConnected = false;
    waitingForReconnect = started;
    subscriptions.forEach((subscription) => {
      subscription.stompSubscription = null;
    });
    if (lostConnection && started) {
      gapStartedAt = lastFrameAt;
      hiddenDuringGap = hiddenDuringGap || document.visibilityState === "hidden";
    }
    publishState({
      ...state,
      status: disconnectedStatus(),
      disconnectedAt: lostConnection ? Date.now() : state.disconnectedAt,
    });
  },
  onHeartbeatReceived: () => {
    lastFrameAt = Date.now();
  },
  onStompError: (frame) => {
    console.error("Socket error", frame.headers["message"]);
    refreshTokensIfExpired();
  },
});

const attach = (subscription: SocketSubscription) => {
  if (!client.connected || subscription.stompSubscription) {
    return;
  }
  subscription.stompSubscription = client.subscribe(subscription.destination, (message) => {
    lastFrameAt = Date.now();
    deliver(subscription, message);
  });
  subscription.onSubscribed?.();
};

const isStale = () => isConnected && Date.now() - lastFrameAt > STALE_AFTER_MS;

const reconnectNow = () => {
  waitingForReconnect = false;
  client
    .deactivate({ force: true })
    .catch(() => undefined)
    .then(() => {
      if (started) {
        client.activate();
      }
    });
};

const reconnectIfStale = () => {
  if (!started) {
    return;
  }
  if (waitingForReconnect || isStale()) {
    reconnectNow();
  }
};

const handleOnline = () => {
  publishState({ ...state, status: isConnected ? "connected" : disconnectedStatus() });
  reconnectIfStale();
};

const handleOffline = () => {
  publishState({ ...state, status: isConnected ? "connected" : disconnectedStatus() });
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "hidden") {
    hiddenDuringGap = hiddenDuringGap || !isConnected;
    return;
  }
  hiddenDuringGap = hiddenDuringGap || isStale();
  reconnectIfStale();
};

const start = () => {
  if (started) {
    return;
  }
  started = true;
  publishState({ ...state, status: "connecting", disconnectedAt: null });
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  client.activate();
};

const stop = () => {
  if (!started) {
    return;
  }
  started = false;
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  client.deactivate({ force: true }).catch(() => undefined);
};

const subscribe = (destination: string, onMessage: (message: SocketMessage) => void, onSubscribed?: () => void) => {
  const subscription: SocketSubscription = { destination, onMessage, onSubscribed, stompSubscription: null };
  subscriptions.add(subscription);
  attach(subscription);
  return () => {
    subscriptions.delete(subscription);
    if (subscription.stompSubscription && client.connected) {
      subscription.stompSubscription.unsubscribe();
    }
    subscription.stompSubscription = null;
  };
};

const publish = (destination: string, body: unknown) => {
  if (!client.connected) {
    return;
  }
  client.publish({ destination, body: JSON.stringify(body) });
};

const onStateChange = (listener: () => void) => {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
};

const onResync = (listener: (resync: SocketResync) => void) => {
  resyncListeners.add(listener);
  return () => {
    resyncListeners.delete(listener);
  };
};

export const socketClient = {
  start,
  stop,
  subscribe,
  publish,
  onStateChange,
  onResync,
  getState: () => state,
  getLastFrameAt: () => lastFrameAt,
};
